import { RESIZE_NUDGE_COARSE_PX, RESIZE_NUDGE_PX, type ResizeDragHandle } from "@/hooks/useResizeDrag";

/**
 * Vertical drag handle shared across Dev-Agent, Weave IDE, and Testing panels.
 * Renders as a 16px wide hit target with a 2px visual track and hover dots.
 *
 * Pass the whole `useResizeDrag` handle as `drag` to get mouse, touch AND keyboard resizing plus
 * correct `aria-value*` reporting. The legacy `onMouseDown`-only form is still accepted so existing
 * call sites keep working, but it cannot be operated by keyboard or touch — prefer `drag`.
 *
 * Keyboard: ←/→ nudge by RESIZE_NUDGE_PX, Shift+←/→ by RESIZE_NUDGE_COARSE_PX,
 * Home/End jump to the min/max, Enter/Space restore the default width.
 *
 * The 16px width is below WCAG 2.2 SC 2.5.8's 24px minimum by design, under the "essential"
 * exception: a splitter's width is part of the layout it divides, and widening it would overlap the
 * panel content on both sides. The keyboard path above is the equivalent, conforming route.
 */
export function ResizeGrip({
  drag,
  onMouseDown,
  title,
}: {
  drag?: ResizeDragHandle;
  /** @deprecated Pass `drag` instead so the handle is keyboard- and touch-operable. */
  onMouseDown?: (e: React.MouseEvent) => void;
  title: string;
}) {
  const handleMouseDown = drag?.onMouseDown ?? onMouseDown;

  function onKeyDown(e: React.KeyboardEvent) {
    if (!drag) return;
    const step = e.shiftKey ? RESIZE_NUDGE_COARSE_PX : RESIZE_NUDGE_PX;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      drag.nudge(-step);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      drag.nudge(step);
    } else if (e.key === "Home") {
      e.preventDefault();
      drag.setWidth(drag.min);
    } else if (e.key === "End") {
      e.preventDefault();
      drag.setWidth(drag.max);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      drag.reset();
    }
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={title}
      aria-valuenow={drag?.width}
      aria-valuemin={drag?.min}
      aria-valuemax={drag?.max}
      tabIndex={drag ? 0 : undefined}
      title={drag ? `${title} — arrow keys to resize, Enter to reset` : title}
      onMouseDown={handleMouseDown}
      onTouchStart={drag?.onTouchStart}
      onKeyDown={onKeyDown}
      className="group relative z-[1] flex w-4 shrink-0 cursor-col-resize touch-none select-none items-stretch justify-center self-stretch border-x border-border/50 bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
    >
      <div className="w-0.5 bg-border transition-colors group-hover:bg-primary/60 group-focus-visible:bg-primary group-active:bg-primary" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        <div className="h-0.5 w-0.5 rounded-full bg-primary-foreground" />
        <div className="h-0.5 w-0.5 rounded-full bg-primary-foreground" />
        <div className="h-0.5 w-0.5 rounded-full bg-primary-foreground" />
        <div className="h-0.5 w-0.5 rounded-full bg-primary-foreground" />
      </div>
    </div>
  );
}
